import React, { useState } from 'react';
import { Alert, Button, Spinner } from '@billda/design-system';
import { AddIcon } from '@billda/design-system';
import { AssignedSiteCard } from './AssignedSiteCard';
import { AddSitesPopover } from './AddSitesPopover';
import { RemoveSiteConfirmPopover } from './RemoveSiteConfirmPopover';
import {
  useUserSiteAssignments,
  useAssignSitesToUser,
  useRemoveUserFromSite,
} from '../hooks/useSiteAssignments';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

interface UserSiteAssignmentsProps {
  userId: string;
  userName: string;
  isManaging: boolean;
  canManage: boolean;
}

export const UserSiteAssignments: React.FC<UserSiteAssignmentsProps> = ({
  userId,
  userName,
  isManaging,
  canManage,
}) => {
  const [addAnchorEl, setAddAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [removeAnchorEl, setRemoveAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [removingSiteId, setRemovingSiteId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showSnackbar = useSnackbarStore(s => s.showSnackbar);

  const {
    data: assignments = [],
    isLoading,
    error: loadError,
  } = useUserSiteAssignments(userId);

  const assignMutation = useAssignSitesToUser();

  const removeMutation = useRemoveUserFromSite();

  React.useEffect(() => {
    if (!isManaging) {
      setAddAnchorEl(null);
      setRemoveAnchorEl(null);
      setRemovingSiteId(null);
    }
  }, [isManaging]);

  const handleAddSites = (siteIds: number[]) => {
    // Get current site IDs and merge with new selections
    const currentSiteIds = assignments.map(a => a.siteId);
    const allSiteIds = [...new Set([...currentSiteIds, ...siteIds])];

    assignMutation.mutate(
      { userId, siteIds: allSiteIds },
      {
        onSuccess: () => {
          setAddAnchorEl(null);
          setError(null);
          showSnackbar('Sites assigned successfully');
        },
        onError: error => {
          setError(
            error instanceof Error
              ? error.message
              : 'Failed to assign user to sites'
          );
        },
      }
    );
  };

  const handleRemoveClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    siteId: number
  ) => {
    setRemoveAnchorEl(event.currentTarget);
    setRemovingSiteId(siteId);
  };

  const handleConfirmRemove = () => {
    if (removingSiteId) {
      removeMutation.mutate(
        { userId, siteId: removingSiteId },
        {
          onSuccess: () => {
            setRemovingSiteId(null);
            setRemoveAnchorEl(null);
            setError(null);
            showSnackbar('Site access removed');
          },
          onError: error => {
            setError(
              error instanceof Error
                ? error.message
                : 'Failed to remove user from site'
            );
            setRemovingSiteId(null);
            setRemoveAnchorEl(null);
          },
        }
      );
    }
  };

  const handleCancelRemove = () => {
    setRemoveAnchorEl(null);
    setRemovingSiteId(null);
  };

  const assignedSiteIds = assignments.map(a => a.siteId);

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div data-testid="user-site-assignments">
      {(error || loadError) && (
        <Alert severity="error" onClose={() => setError(null)} className="mb-4">
          {error ||
            (loadError instanceof Error
              ? loadError.message
              : String(loadError))}
        </Alert>
      )}

      <div className="flex flex-col">
        {assignments.length === 0 && (
          <p className="type-body-md py-2 text-content-secondary">
            No sites assigned
          </p>
        )}
        {assignments.map(assignment => (
          <AssignedSiteCard
            key={assignment.siteId}
            assignment={assignment}
            onRemoveClick={handleRemoveClick}
            isRemoving={
              removingSiteId === assignment.siteId && removeMutation.isPending
            }
            canManage={canManage && isManaging}
          />
        ))}

        {canManage && isManaging && (
          <Button
            variant="outline"
            onClick={e => setAddAnchorEl(e.currentTarget)}
            data-testid="add-sites-button"
            className="mt-4 self-start"
          >
            <AddIcon aria-hidden="true" className="size-4" />
            Add sites
          </Button>
        )}
      </div>

      <AddSitesPopover
        anchorEl={addAnchorEl}
        onClose={() => setAddAnchorEl(null)}
        onAddSites={handleAddSites}
        userName={userName}
        assignedSiteIds={assignedSiteIds}
        isSubmitting={assignMutation.isPending}
      />

      <RemoveSiteConfirmPopover
        anchorEl={removeAnchorEl}
        siteName={
          removingSiteId
            ? assignments.find(a => a.siteId === removingSiteId)?.siteName || ''
            : ''
        }
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        isRemoving={removeMutation.isPending}
      />
    </div>
  );
};
